/**
 * Layout.tsx
 *
 * File purpose:
 * - Provides the main page layout used across the app.
 * - Renders Header, page content (children), a centralized SEO FAQ renderer (placed above Footer),
 *   and the Footer. Placing FAQs here ensures all pages display their FAQ blocks directly above the Footer
 *   while keeping the existing visual structure unchanged.
 */

import React from 'react'
import Header from './Header'
import Footer from './Footer'
import SeoFAQRenderer from './SeoFAQRenderer'

/**
 * LayoutProps
 * @interface
 * @property children - React children to render inside the layout
 */
interface LayoutProps {
  children: React.ReactNode
}

/**
 * Layout
 *
 * Purpose:
 * - Wrap page content with header and footer.
 * - Render the SEO FAQ block right before the Footer so every page shows its FAQ above the footer area.
 *
 * @param {LayoutProps} props - component props
 * @returns {JSX.Element}
 */
export default function Layout({ children }: LayoutProps): JSX.Element {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>

      {/* Render page-specific FAQ blocks above the footer to keep UI consistent across pages */}
      <SeoFAQRenderer />

      <Footer />
    </div>
  )
}
